import React from "react";

/* A tool's illustrated tile, the same everywhere it appears: the app Home
   grid, a tool page's head and the solstack.xyz/tools directory (the app
   sidebar keeps its smaller line icons). One pictogram per tool id on a 48-unit grid, drawn the way the
   site has drawn everything since the neobrutalism restyle — flat fills, a
   black rule and a hard black shadow offset down-right — on a tile in its
   family's colour (src/solstack/lib/toolCatalog.js). The three
   bonding-curve launchers show their platform's own logo instead: the brand
   is the fastest way to tell them apart. Tile styles: .tool-tile in
   src/style/theme.css.

   Strokes and shadows are always black, so the art reads the same in dark
   mode, where the site keeps black borders on charcoal. No star or sparkle
   shapes — that art was removed app-wide. */

const INK = "#000";
const PAPER = "#fff";
const YELLOW = "#ffd23f";
const LIME = "#c7f24c";
const BLUE = "#5ad2f5";
const DEEP = "#2b9fd6";
const GREEN = "#7ceb9a";
const VIOLET = "#c9a7ff";
const ORANGE = "#ffa64d";
const RED = "#ff7a6b";

// The rule every outline is drawn with, and how far shadows fall. The
// strokeWidths below are written for a 2.5 rule; line() scales them.
const LINE = 1.75;
const OFFSET = 2;
const line = (w) => (w * LINE) / 2.5;

/** One object built from parts in different colours. Its shadow is a
 * single black copy of every part, shifted down-right and drawn under all
 * of them, so the parts of one object never shadow each other. `parts` is
 * a list of [fill, shapes], back to front; shapes must not set their own
 * fill. `t` is a transform applied inside each copy, so a rotated object
 * still casts its shadow straight down-right. Separate objects (a badge on
 * a coin, a lens over a receipt) are separate Solids, so the one in front
 * does shadow the one behind. */
const Solid = ({ t, parts }) => {
  const place = (shapes) => (t ? <g transform={t}>{shapes}</g> : shapes);
  return (
    <>
      <g transform={`translate(${OFFSET} ${OFFSET})`} fill={INK}>
        {parts.map(([, shapes], i) => (
          <React.Fragment key={i}>{place(shapes)}</React.Fragment>
        ))}
      </g>
      {parts.map(([fill, shapes], i) => (
        <g key={i} fill={fill}>
          {place(shapes)}
        </g>
      ))}
    </>
  );
};

/** A one-colour object: its shapes, white unless `fill` says otherwise. */
const Lift = ({ fill = PAPER, t, children }) => <Solid t={t} parts={[[fill, children]]} />;

/** A stroked line given thickness: an outlined tube with a shadow, for
 * shapes like a padlock's shackle that are open paths, not areas. */
const Tube = ({ d, w = 8, fill = PAPER }) => (
  <>
    <path d={d} transform={`translate(${OFFSET} ${OFFSET})`} strokeWidth={w} strokeLinecap="butt" />
    <path d={d} strokeWidth={w} strokeLinecap="butt" />
    <path d={d} stroke={fill} strokeWidth={w - 2 * LINE} strokeLinecap="butt" />
  </>
);

/* ---- shared pieces ---- */

// `shine` adds a glint along the upper left of the rim.
const Coin = ({ cx, cy, r, fill = PAPER, shine = false }) => (
  <>
    <Lift fill={fill}>
      <circle cx={cx} cy={cy} r={r} />
    </Lift>
    <circle cx={cx} cy={cy} r={r - 4.5} />
    {shine && (
      <path
        d={`M${cx - r * 0.72} ${cy - r * 0.08}a${r * 0.72} ${r * 0.72} 0 0 1 ${r * 0.5} ${-r * 0.6}`}
        stroke={PAPER}
        strokeWidth={line(1.8)}
      />
    )}
  </>
);

// The Solana mark's three slanted bars, on the face of a coin.
const CoinBars = ({ cx, cy }) => (
  <path
    d={`M${cx - 3.5} ${cy - 5.8}h9l-2.5 3h-9zM${cx - 5.5} ${cy - 1.5}h9l2.5 3h-9zM${cx - 3.5} ${cy + 2.8}h9l-2.5 3h-9z`}
    fill={INK}
    stroke="none"
  />
);

const Padlock = ({ x = 6, y = 20, w = 30, h = 23 }) => {
  const cx = x + w / 2;
  const r = w * 0.3;
  return (
    <>
      <Tube d={`M${cx - r} ${y + 1}V${y - 5}a${r} ${r} 0 0 1 ${2 * r} 0V${y + 1}`} />
      <Lift>
        <rect x={x} y={y} width={w} height={h} rx="3.5" />
      </Lift>
    </>
  );
};

const Keyhole = ({ cx, cy }) => (
  <>
    <circle cx={cx} cy={cy} r="2.8" fill={INK} stroke="none" />
    <path d={`M${cx} ${cy + 1}v5.5`} strokeWidth={line(3)} />
  </>
);

const CheckBadge = ({ cx = 37, cy = 36 }) => (
  <>
    <Lift fill={GREEN}>
      <circle cx={cx} cy={cy} r="8.5" />
    </Lift>
    <path d={`M${cx - 4} ${cy}l2.8 2.8 5-5.3`} strokeWidth={line(2.8)} />
  </>
);

const Droplet = ({ fill = BLUE }) => (
  <>
    <Lift fill={fill}>
      <path d="M21 4c6 8 13 15 13 23.5a13 13 0 0 1-26 0C8 19 15 12 21 4z" />
    </Lift>
    <path d="M14.5 28a6.5 6.5 0 0 0 5 6.5" stroke={PAPER} strokeWidth={line(2.5)} />
  </>
);

// Water filling the bottom of the tile up to `level`: a rolling surface, a
// deeper band below it and two glints. `splash` adds the crown a coin
// throws up, as part of the water so the two cast one shadow.
const Water = ({ level, splash = false }) => {
  // The deep band sits just inside the water's outline.
  const x0 = 2 + LINE / 2;
  const q = (44 - LINE) / 4;
  const r = 3 - LINE / 2;
  return (
    <>
      <Solid
        parts={[
          [
            BLUE,
            <>
              {splash && (
                <path
                  d={`M7 ${level + 2}c1-4 0-8.5 2.5-12a3 3 0 0 1 5.2.8c.6 3.8 1.8 7.5 4.6 10.2zM41 ${level + 2}c-1-4 0-8.5-2.5-12a3 3 0 0 0-5.2.8c-.6 3.8-1.8 7.5-4.6 10.2z`}
                />
              )}
              <path d={`M2 ${level}q5.5-4.2 11 0t11 0 11 0 11 0V43q0 3-3 3H5q-3 0-3-3z`} />
            </>,
          ],
          [
            DEEP,
            <path
              d={`M${x0} ${level + 8.5}q${q / 2}-2.8 ${q} 0t${q} 0 ${q} 0 ${q} 0V${46 - LINE / 2 - r}q0 ${r}-${r} ${r}H${x0 + r}q-${r} 0-${r}-${r}z`}
              stroke="none"
            />,
          ],
        ]}
      />
      <path d={`M7 ${level + 4.2}h5M29.5 ${level + 5.2}h7.5`} stroke={PAPER} strokeWidth={line(2.2)} />
    </>
  );
};

// A drop in flight. No shadow: at this size one would swallow the blue.
const FlatDrop = ({ x, y, rot = 0, s = 1 }) => (
  <path
    transform={`translate(${x} ${y}) rotate(${rot}) scale(${s})`}
    d="M0-4.5c1.8 2.4 3 4 3 5.6a3 3 0 0 1-6 0c0-1.6 1.2-3.2 3-5.6z"
    fill={BLUE}
    strokeWidth={line(2.2) / s}
  />
);

/* ---- one pictogram per tool id (TOOL_PATHS keys) ---- */

const ART = {
  "create-spl": () => (
    <>
      <Coin cx={21} cy={26} r={15} />
      <CoinBars cx={20.5} cy={26} />
      <Lift fill={LIME}>
        <circle cx="36" cy="12" r="8.5" />
      </Lift>
      <path d="M36 8v8M32 12h8" strokeWidth={line(3)} />
    </>
  ),

  "create-spl22": () => (
    <>
      <Coin cx={21} cy={26} r={15} />
      <CoinBars cx={20.5} cy={26} />
      <Lift fill={VIOLET}>
        <circle cx="36" cy="12" r="8.5" />
      </Lift>
      <path d="M32.5 15.5l7-7" strokeWidth={line(2.5)} />
      <circle cx="33" cy="9" r="1.9" fill={INK} stroke="none" />
      <circle cx="39" cy="15" r="1.9" fill={INK} stroke="none" />
    </>
  ),

  // Drawn upright, then tilted to fly up and to the right.
  launchpad: () => {
    const t = "rotate(45 24 24)";
    return (
      <>
        <Solid
          t={t}
          parts={[
            [ORANGE, <path d="M19.5 32c0 5 2 8.5 4.5 11 2.5-2.5 4.5-6 4.5-11z" />],
            [RED, <path d="M18 22l-7.5 7.5V36l8-3.5zM30 22l7.5 7.5V36l-8-3.5z" />],
            [PAPER, <path d="M24 3c6.5 5.5 8.5 15.5 6.5 30h-13c-2-14.5 0-24.5 6.5-30z" />],
          ]}
        />
        <g transform={t}>
          <circle cx="24" cy="17" r="4" fill={BLUE} />
        </g>
      </>
    );
  },

  stonkfun: () => (
    <>
      <Lift>
        <rect x="9" y="17" width="6" height="18" />
        <rect x="21" y="17" width="6" height="18" />
        <rect x="33" y="17" width="6" height="18" />
        <path d="M5 17L24 6l19 11z" />
        <rect x="5" y="34" width="38" height="7" rx="1.5" />
      </Lift>
      <circle cx="24" cy="12.5" r="2.4" />
    </>
  ),

  "creator-fees": () => (
    <>
      <Lift fill={YELLOW}>
        <rect x="10" y="35" width="26" height="7" rx="3.5" />
        <rect x="13" y="28" width="26" height="7" rx="3.5" />
        <rect x="11" y="21" width="26" height="7" rx="3.5" />
      </Lift>
      <path d="M24 4v11M19 10.5l5 5 5-5" strokeWidth={line(3.5)} />
    </>
  ),

  // A coin splashing into the pool, and one lifted back out of a pool run
  // lower, still dripping: the two tools are the same pool, either way round.
  // The water is drawn after the falling coin, so it hides the coin's foot.
  "add-liquidity": () => (
    <>
      <Coin cx={24} cy={19.5} r={9.5} fill={YELLOW} shine />
      <Water level={30} splash />
      <FlatDrop x={6} y={11} rot={-30} />
      <FlatDrop x={42} y={11} rot={30} />
      <path d="M18 5V2M24 3.5V0M30 5V2" strokeWidth={line(2.5)} />
    </>
  ),

  "remove-liquidity": () => (
    <>
      <Water level={32} />
      <Coin cx={24} cy={12.5} r={9.5} fill={YELLOW} shine />
      <FlatDrop x={18.5} y={27} />
      <FlatDrop x={25} y={29} s={0.85} />
      <path d="M8 22V11M40 22V11M4.5 14.5L8 11l3.5 3.5M36.5 14.5L40 11l3.5 3.5" strokeWidth={line(2.8)} />
    </>
  ),

  // A price ladder, coloured like the tool's own preview: the slot above
  // the price sells (blue), the slots below buy (lime), and the price
  // zig-zags across the lines to where it is now (yellow).
  "grid-bot": () => (
    <>
      <Lift>
        <rect x="4" y="6" width="40" height="36" rx="3.5" />
      </Lift>
      <rect x={4 + LINE / 2} y="11" width={40 - LINE} height="9" fill={BLUE} stroke="none" />
      <rect x={4 + LINE / 2} y="20" width={40 - LINE} height="18" fill={LIME} stroke="none" />
      <path
        d={[11, 20, 29, 38].map((y) => `M${4 + LINE / 2} ${y}h${40 - LINE}`).join("")}
        strokeWidth={line(2)}
        strokeLinecap="butt"
      />
      <path d="M9 33l7-18 7 19 7-18 6 9" strokeWidth={line(3)} />
      <circle cx="36" cy="25" r="3.6" fill={YELLOW} />
    </>
  ),

  // Meteora DLMM bins around the price: SOL in the bins below it (lime),
  // the token in the bins above (blue), the active bin yellow under a
  // marker for the price now — as the tool's position preview draws them.
  "liquidity-bot": () => (
    <>
      <Solid
        parts={[
          [
            LIME,
            <>
              <rect x="3" y="31" width="6" height="11" rx="1.5" />
              <rect x="12" y="23" width="6" height="19" rx="1.5" />
            </>,
          ],
          [YELLOW, <rect x="21" y="14" width="6" height="28" rx="1.5" />],
          [
            BLUE,
            <>
              <rect x="30" y="23" width="6" height="19" rx="1.5" />
              <rect x="39" y="31" width="6" height="11" rx="1.5" />
            </>,
          ],
        ]}
      />
      <path d="M19.5 4h9L24 9.5z" fill={INK} />
    </>
  ),

  multisender: () => (
    <>
      <path d="M14 24l17-13M14 24h17M14 24l17 13" />
      <Coin cx={11} cy={24} r={8} fill={YELLOW} />
      <Lift>
        <rect x="31" y="6" width="12" height="10" rx="2.5" />
        <rect x="31" y="19" width="12" height="10" rx="2.5" />
        <rect x="31" y="32" width="12" height="10" rx="2.5" />
      </Lift>
    </>
  ),

  airdrop: () => (
    <>
      <path d="M7 21l11 11M41 21L30 32M24 19v13" />
      <Lift>
        <path d="M7 21C7 11 15 5 24 5s17 6 17 16q-5.7-4-11.3 0-5.7-4-11.4 0-5.6-4-11.3 0z" />
      </Lift>
      <path d="M18.3 21c0-8 2.2-13 5.7-16M29.7 21c0-8-2.2-13-5.7-16" />
      <Lift fill={YELLOW}>
        <rect x="16.5" y="31" width="15" height="12" rx="1.5" />
      </Lift>
      <path d="M24 31v12" />
    </>
  ),

  "wallet-aggregator": () => (
    <>
      <path d="M12 10l15 10M12 24h15M12 38l15-10" />
      <Lift fill={YELLOW}>
        <circle cx="9" cy="10" r="5" />
        <circle cx="9" cy="24" r="5" />
        <circle cx="9" cy="38" r="5" />
      </Lift>
      <Lift>
        <rect x="24" y="12" width="19" height="24" rx="3.5" />
      </Lift>
      <rect x="33" y="19" width="10" height="10" rx="2" fill={LIME} />
      <circle cx="37.5" cy="24" r="1.7" fill={INK} stroke="none" />
    </>
  ),

  manage: () => (
    <>
      <Lift>
        <rect x="6" y="7" width="36" height="34" rx="3.5" />
      </Lift>
      <path d="M12 15h24M12 24h24M12 33h24" />
      <circle cx="28" cy="15" r="4" fill={YELLOW} />
      <circle cx="18" cy="24" r="4" fill={YELLOW} />
      <circle cx="31" cy="33" r="4" fill={YELLOW} />
    </>
  ),

  burn: () => (
    <>
      <Lift fill={ORANGE}>
        <path d="M24 4c1.5 7 9 11 11.5 19 2.5 9-3.5 20-11.5 20s-13.5-6-13.5-13.5c0-6.5 4-10 6.5-15 2.5 3 3.5 6 3.5 9 3-4.5 5-11.5 3.5-19.5z" />
      </Lift>
      <path d="M24 24c2.5 3.5 6 6 6 10 0 3-2.5 5.5-6 5.5s-6-2.5-6-5.5c0-4 3.5-6.5 6-10z" fill={YELLOW} />
    </>
  ),

  "rent-reclaim": () => (
    <>
      <Coin cx={24} cy={24} r={9.5} fill={YELLOW} />
      <path
        d="M8 24A16 16 0 0 1 35.3 12.7M35.3 5.6v7.1h-7.1M40 24a16 16 0 0 1-27.3 11.3M12.7 42.4v-7.1h7.1"
        strokeWidth={line(3)}
      />
    </>
  ),

  "wallet-cleaner": () => {
    const t = "rotate(28 24 24)";
    return (
      <>
        <Solid
          t={t}
          parts={[
            [ORANGE, <rect x="22" y="1" width="4" height="22" rx="2" />],
            [YELLOW, <path d="M15.5 27h17l4.5 15H11z" />],
            [PAPER, <rect x="15" y="21" width="18" height="6" rx="1.5" />],
          ]}
        />
        <path d="M20 31l-1.5 10M24 31v10M28 31l1.5 10" transform={t} />
        <circle cx="39" cy="41" r="1.8" fill={INK} stroke="none" />
        <circle cx="43" cy="36" r="1.4" fill={INK} stroke="none" />
      </>
    );
  },

  // The EVM Dust Cleaner: coins left on other chains, gathered into one SOL
  // coin. It lives on its own host, so it has no TOOL_PATHS key.
  "evm-dust-cleaner": () => (
    <>
      <path d="M8 10L31 25M8 24L31 25M8 38L31 25" />
      <Lift fill={BLUE}>
        <circle cx="8" cy="10" r="5" />
      </Lift>
      <Lift fill={VIOLET}>
        <circle cx="8" cy="24" r="5" />
      </Lift>
      <Lift fill={ORANGE}>
        <circle cx="8" cy="38" r="5" />
      </Lift>
      <Coin cx={31} cy={25} r={13} />
      <CoinBars cx={30.5} cy={25} />
    </>
  ),

  // The multisend jobs dashboard: a clipboard of runs, two done, one to go.
  "send-jobs": () => (
    <>
      <Lift>
        <rect x="8" y="7" width="32" height="36" rx="3" />
      </Lift>
      <Lift fill={YELLOW}>
        <rect x="17" y="3.5" width="14" height="7" rx="2" />
      </Lift>
      <rect x="13.5" y="16" width="6" height="6" rx="1" fill={GREEN} />
      <rect x="13.5" y="25" width="6" height="6" rx="1" fill={GREEN} />
      <rect x="13.5" y="34" width="6" height="6" rx="1" fill={PAPER} />
      <path d="M24 19h10M24 28h10M24 37h7" />
    </>
  ),

  "token-locker": () => (
    <>
      <Padlock x={9} y={20} w={30} h={23} />
      <circle cx="24" cy="31.5" r="6.5" fill={YELLOW} />
      <path d="M24 27.5v4l3 2" />
    </>
  ),

  "token-lock-verifier": () => (
    <>
      <Padlock x={5} y={20} w={30} h={23} />
      <Keyhole cx={20} cy={29} />
      <CheckBadge cx={37} cy={36} />
    </>
  ),

  "lp-locker": () => (
    <>
      <Droplet />
      <Tube d="M32.5 31v-3a4.5 4.5 0 0 1 9 0v3" w={7} />
      <Lift>
        <rect x="28" y="30" width="18" height="13" rx="2.5" />
      </Lift>
      <circle cx="37" cy="36.5" r="2" fill={INK} stroke="none" />
    </>
  ),

  "lp-lock-verifier": () => (
    <>
      <Droplet />
      <CheckBadge cx={37} cy={36} />
    </>
  ),

  snapshot: () => (
    <>
      <Lift>
        <path d="M5 18q0-4 4-4h6l3.5-6h11l3.5 6h6q4 0 4 4v18q0 4-4 4H9q-4 0-4-4z" />
      </Lift>
      <circle cx="24" cy="27" r="8.5" fill={BLUE} />
      <circle cx="24" cy="27" r="3.8" fill={PAPER} />
      <rect x="34" y="17.5" width="5" height="3.5" rx="1" fill={INK} stroke="none" />
    </>
  ),

  "my-tokens": () => (
    <>
      <Coin cx={30} cy={12} r={7.5} fill={YELLOW} />
      <Coin cx={17} cy={15} r={7.5} fill={YELLOW} />
      <Lift>
        <rect x="5" y="18" width="38" height="24" rx="4" />
      </Lift>
      <rect x="30" y="25" width="13" height="10" rx="2.5" fill={LIME} />
      <circle cx="35.5" cy="30" r="1.8" fill={INK} stroke="none" />
    </>
  ),

  "token-inspector": () => (
    <>
      <Lift fill={GREEN}>
        <path d="M24 4l16 6v12c0 10-6.5 17-16 22C14.5 39 8 32 8 22V10z" />
      </Lift>
      <path d="M16.5 24l5.5 5.5 10-11" strokeWidth={line(3.5)} />
    </>
  ),

  "smart-money": () => (
    <>
      <Lift fill={GREEN}>
        <rect x="7" y="30" width="8" height="11" rx="1.5" />
        <rect x="19" y="24" width="8" height="17" rx="1.5" />
        <rect x="31" y="17" width="8" height="24" rx="1.5" />
      </Lift>
      <path d="M6 22l9-8 7 4 15-12M30 6h7v7" strokeWidth={line(3)} />
    </>
  ),

  "stonkfun-rewards": () => (
    <>
      <Solid
        parts={[
          [RED, <path d="M24 15c-4-9-12-8-10-3 1 2.5 5 3 10 3zM24 15c4-9 12-8 10-3-1 2.5-5 3-10 3z" />],
          [
            PAPER,
            <>
              <rect x="8" y="22" width="32" height="20" rx="1.5" />
              <rect x="5" y="15" width="38" height="8" rx="1.5" />
            </>,
          ],
          [RED, <rect x="21" y="15" width="6" height="27" />],
        ]}
      />
    </>
  ),

  "tx-inspector": () => (
    <>
      <Lift>
        <path d="M8 5h24v36l-3-2.5-3 2.5-3-2.5-3 2.5-3-2.5-3 2.5-3-2.5-3 2.5z" />
      </Lift>
      <path d="M13 12h14M13 18h14M13 24h8" />
      <path d="M37.5 35.5L43 41" strokeWidth={line(5.5)} />
      <Lift fill={BLUE}>
        <circle cx="32" cy="30" r="7.5" />
      </Lift>
      <path d="M28.5 29a4 4 0 0 1 3-3.5" stroke={PAPER} />
    </>
  ),

  account: () => (
    <>
      <Lift>
        <rect x="4" y="10" width="40" height="29" rx="3.5" />
      </Lift>
      <path d="M8.5 33q0-6 6.5-6t6.5 6z" fill={YELLOW} />
      <circle cx="15" cy="20.5" r="4" fill={YELLOW} />
      <path d="M27 18h11M27 24h11M27 30h7" />
    </>
  ),

  status: () => (
    <>
      <Lift>
        <path d="M4 31a20 20 0 0 1 40 0z" />
        <rect x="4" y="31" width="40" height="6" rx="1.5" />
      </Lift>
      <path d="M11.4 23.8l-2.6-1.5M16.8 18.4l-1.5-2.6M24 16.5v-3M31.2 18.4l1.5-2.6M36.6 23.8l2.6-1.5" />
      <path d="M24 31l10-12" strokeWidth={line(3.5)} />
      <circle cx="24" cy="31" r="3.5" fill={INK} stroke="none" />
    </>
  ),

  sign: () => (
    <>
      <Lift>
        <rect x="6" y="5" width="27" height="37" rx="2.5" />
      </Lift>
      <path d="M11 12h17M11 18h17M11 24h10" />
      <path d="M11 34c2-5 4 3 6.5-1.5 1.5-2.5 3 2.5 5.5.5" strokeWidth={line(2)} />
      <Solid
        parts={[
          [PAPER, <path d="M23 21l6 6-8.5 2.5z" />],
          [YELLOW, <path d="M37 7l6 6-14 14-6-6z" />],
        ]}
      />
    </>
  ),

  vanity: () => (
    <>
      <Lift fill={YELLOW}>
        <path d="M21 21h22v6h-2.5v5.5H36V27h-2.5v4h-4.5v-4H21z" />
        <circle cx="14" cy="24" r="9.5" />
      </Lift>
      <circle cx="11.5" cy="24" r="3" fill={PAPER} />
    </>
  ),

  faucet: () => (
    <>
      {/* handle, stem, spout and the plate fixing it to the wall: one object */}
      <Lift>
        <rect x="15.5" y="9.5" width="5" height="6" />
        <rect x="10" y="5" width="16" height="4.5" rx="2.25" />
        <path d="M6 15h21c6 0 10 4 10 10v5h-8v-5c0-1.1-.9-2-2-2H6z" />
        <rect x="3" y="12" width="5" height="14" rx="1.5" />
      </Lift>
      <Lift fill={BLUE}>
        <path d="M33 33c2 3.5 4 5.5 4 7.5a4 4 0 0 1-8 0c0-2 2-4 4-7.5z" />
      </Lift>
    </>
  ),
};

/* Platform logos for the launchers that front a named platform. */
const LOGOS = {
  launch: "/icons/platforms/pumpfun.png",
  "launch-bonk": "/icons/platforms/letsbonk.png",
  "launch-meteora": "/icons/platforms/meteora.svg",
};

/** The tool's pictogram on its family-coloured tile. `color` is the
 * family's colour; `size` is "md" (cards, page heads) or "lg" (the /tools
 * hero). */
export const ToolTile = ({ id, color, size = "md", className = "" }) => {
  const Art = ART[id];
  const logo = LOGOS[id];
  return (
    <span
      className={`tool-tile tool-tile--${size} ${className}`.trim()}
      style={{ "--fam": color }}
      aria-hidden="true"
    >
      {logo ? (
        <img className="tool-tile-logo" src={logo} alt="" loading="lazy" decoding="async" />
      ) : Art ? (
        <svg
          viewBox="0 0 48 48"
          fill="none"
          stroke={INK}
          strokeWidth={LINE}
          strokeLinecap="round"
          strokeLinejoin="round"
          focusable="false"
        >
          <Art />
        </svg>
      ) : null}
    </span>
  );
};
